/**
 * <sticky-add-to-cart> — Fixed bottom bar shown when main ATC scrolls out of view.
 *
 * Uses IntersectionObserver to watch the product form's ATC button.
 * When the button leaves the viewport, the sticky bar becomes visible.
 *
 * The bar's button is labelled "Buy it now", so it clicks the real dynamic
 * checkout button in the main form rather than the add-to-cart submit —
 * otherwise the label would promise checkout and silently do something else.
 * When dynamic checkout is unavailable it falls back to the form's submit.
 *
 * Rendered by sections/main-product.liquid.
 */

class StickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.targetSelector = this.dataset.observeTarget;
    this.mainForm = document.getElementById(this.dataset.formId);

    if (!this.targetSelector) return;

    this.target = document.querySelector(this.targetSelector);
    if (!this.target) return;

    this.submitBtn = this.querySelector('.sticky-atc__button');
    if (this.submitBtn) {
      this.handleClick = this.handleClick.bind(this);
      this.submitBtn.addEventListener('click', this.handleClick);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.classList.remove('is-visible');
          } else {
            this.classList.add('is-visible');
          }
        }
      },
      { threshold: 0 }
    );

    this.observer.observe(this.target);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.submitBtn?.removeEventListener('click', this.handleClick);
  }

  handleClick() {
    const { paymentSelector } = this.dataset;
    const paymentButton = paymentSelector ? document.querySelector(paymentSelector) : null;

    if (paymentButton) {
      paymentButton.click();
      return;
    }

    // Dynamic checkout is off or still hydrating — fall back to add to cart.
    const mainSubmit = this.mainForm?.querySelector('[type="submit"], button[name="add"]');
    mainSubmit?.click();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
